import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Target, Plus, Trash, PencilSimple, Warning, Sparkle, Wallet, CheckCircle, Calculator, TrendUp, Gear } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = ["🎯", "🏖️", "🚗", "🏠", "🎓", "💍", "✈️", "💰", "🎄", "👶"];
const CALC_LABEL = { hourly: "Per hour", per_shift: "Per shift", per_project: "Per project", per_assignment: "Per assignment", fixed: "Fixed amount" };
const today = () => new Date().toISOString().slice(0, 10);
const STATUS = {
  reached: { label: "Reached 🎉", cls: "bg-emerald-500/15 text-emerald-400" },
  overdue: { label: "Deadline passed", cls: "bg-rose-500/15 text-rose-400" },
  on_track: { label: "In progress", cls: "bg-primary/15 text-primary" },
};

const calcDesc = (t, c) => {
  if (t.calc_type === "hourly") return `${money(t.rate, c)}/hr`;
  if (t.calc_type === "per_shift") return t.typical_hours > 0 ? `${money(t.rate, c)}/hr × ${t.typical_hours}h = ${money(t.unit_amount, c)}/shift` : `${money(t.unit_amount, c)}/shift`;
  return `${money(t.unit_amount, c)} / ${CALC_LABEL[t.calc_type].replace("Per ", "")}`;
};

export default function GoalPlanner() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["goals"], queryFn: async () => (await api.get("/goals")).data });
  const [showNew, setShowNew] = useState(false);

  const goals = data?.goals || [];
  const cur = data?.rates?.currency || "CAD";
  const etypes = data?.earning_types || [];
  const activeCount = etypes.filter((t) => t.active).length;

  return (
    <div className="animate-fade-up space-y-6" data-testid="goal-planner-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center"><Target className="h-6 w-6 text-primary" weight="fill" /></div>
          <div>
            <p className="overline text-primary">Earn toward a goal</p>
            <h1 className="text-3xl font-black tracking-tight">Goal Planner</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <EarningsManager currency={cur} />
          <Button onClick={() => setShowNew((s) => !s)} data-testid="new-goal-btn"><Plus className="h-4 w-4 mr-1.5" weight="bold" /> New goal</Button>
        </div>
      </div>

      {activeCount === 0 && (
        <Card className="p-4 border-amber-500/30 bg-amber-500/5 flex items-start gap-2" data-testid="no-earnings-warn">
          <Warning className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">Add at least one <span className="text-foreground font-medium">extra earning type</span> (Overtime, Paid Duty, a project, an extra shift…) so we can show you how to reach your goals. Use <span className="text-foreground">Extra earnings</span> above.</p>
        </Card>
      )}

      {showNew && <GoalForm currency={cur} onDone={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ["goals"] }); }} />}

      {isLoading ? <p className="text-muted-foreground text-sm py-8 text-center">Loading your goals…</p>
        : goals.length === 0 && !showNew ? (
          <Card className="p-10 text-center" data-testid="goals-empty">
            <Target className="h-10 w-10 text-primary mx-auto mb-3" weight="duotone" />
            <p className="font-bold text-lg">Set your first goal</p>
            <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">Tell MyPayAlert what you want and by when — we'll show exactly how much extra work gets you there.</p>
            <Button className="mt-4" onClick={() => setShowNew(true)} data-testid="empty-new-goal"><Plus className="h-4 w-4 mr-1.5" /> Create a goal</Button>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-2 gap-5" data-testid="goals-grid">
            {goals.map((g) => <GoalCard key={g.id} goal={g} currency={cur} />)}
          </div>
        )}

      <WhatIf currency={cur} rates={data?.rates} etypes={etypes.filter((t) => t.active)} />
    </div>
  );
}

/* ---------- Extra earnings manager ---------- */
function EarningsManager({ currency }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: types = [] } = useQuery({ queryKey: ["goal-earnings"], queryFn: async () => (await api.get("/goal-earnings")).data, enabled: open });
  const [f, setF] = useState({ name: "", calc_type: "per_shift", rate: "", typical_hours: "" });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["goal-earnings"] }); qc.invalidateQueries({ queryKey: ["goals"] }); };
  const add = useMutation({
    mutationFn: async () => (await api.post("/goal-earnings", { name: f.name, calc_type: f.calc_type, rate: Number(f.rate), typical_hours: Number(f.typical_hours || 0) })).data,
    onSuccess: () => { refresh(); setF({ name: "", calc_type: "per_shift", rate: "", typical_hours: "" }); toast.success("Earning type added"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const toggle = useMutation({ mutationFn: async ({ id, active }) => api.put(`/goal-earnings/${id}`, { active }), onSuccess: refresh });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/goal-earnings/${id}`), onSuccess: refresh });

  const showHours = f.calc_type === "per_shift" || f.calc_type === "hourly";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="manage-earnings-btn"><Gear className="h-4 w-4 mr-1.5" weight="bold" /> Extra earnings</Button>
      </DialogTrigger>
      <DialogContent className="bg-popover border-border max-w-lg" data-testid="earnings-dialog">
        <DialogHeader><DialogTitle>Your extra earning types</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">These power the "ways to reach your goal" calculations. Add anything: Overtime, Paid Duty, Extra Shift, Special Project, Call-In…</p>

        <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="earnings-list">
          {types.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-2 rounded-md border border-border p-2.5" data-testid={`etype-row-${t.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{t.name} <span className="text-xs text-muted-foreground font-normal">· {CALC_LABEL[t.calc_type]}</span></p>
                <p className="text-xs text-muted-foreground">{calcDesc(t, currency)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={t.active} onCheckedChange={(v) => toggle.mutate({ id: t.id, active: v })} data-testid={`etype-toggle-${t.id}`} />
                <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => del.mutate(t.id)} data-testid={`etype-delete-${t.id}`}><Trash className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
          {types.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No earning types yet — add one below.</p>}
        </div>

        <div className="border-t border-border pt-3 space-y-2">
          <p className="text-sm font-bold flex items-center gap-1.5"><Plus className="h-4 w-4" /> Add extra earning</p>
          <Input placeholder="Name (e.g. Extra Shift, Special Project)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="etype-name" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">How is it paid?</Label>
              <Select value={f.calc_type} onValueChange={(v) => setF({ ...f, calc_type: v })}>
                <SelectTrigger data-testid="etype-calc"><SelectValue /></SelectTrigger>
                <SelectContent className="z-[120]">{Object.entries(CALC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{f.calc_type === "hourly" || f.calc_type === "per_shift" ? "Rate ($/hour)" : "Amount ($)"}</Label>
              <Input type="number" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="etype-rate" />
            </div>
          </div>
          {showHours && (
            <div>
              <Label className="text-xs">Typical hours {f.calc_type === "hourly" ? "(optional)" : "per shift"}</Label>
              <Input type="number" value={f.typical_hours} onChange={(e) => setF({ ...f, typical_hours: e.target.value })} data-testid="etype-hours" />
            </div>
          )}
          <Button className="w-full" onClick={() => add.mutate()} disabled={add.isPending || !f.name || !f.rate} data-testid="etype-add">Add earning type</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Goal form ---------- */
function GoalForm({ currency, onDone, existing }) {
  const [f, setF] = useState(existing || { name: "", icon: "🎯", target_amount: "", current_amount: "0", target_date: "", auto_track: true });
  const save = useMutation({
    mutationFn: async () => {
      const body = { ...f, target_amount: Number(f.target_amount), current_amount: Number(f.current_amount || 0) };
      return existing ? (await api.put(`/goals/${existing.id}`, body)).data : (await api.post("/goals", body)).data;
    },
    onSuccess: () => { toast.success(existing ? "Goal updated" : "Goal created"); onDone(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <Card className="p-5" data-testid="goal-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label>Goal name</Label>
          <div className="flex flex-wrap gap-1 items-center">
            {ICONS.map((ic) => (
              <button key={ic} type="button" onClick={() => setF({ ...f, icon: ic })} data-testid={`goal-icon-${ic}`}
                className={`h-9 w-9 rounded-md text-lg transition-colors ${f.icon === ic ? "bg-primary/20 ring-1 ring-primary" : "hover:bg-secondary"}`}>{ic}</button>
            ))}
          </div>
          <Input className="mt-2" placeholder="e.g. Vacation, New vehicle" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="goal-name" />
        </div>
        <div><Label>Goal amount ({currency})</Label><Input type="number" value={f.target_amount} onChange={(e) => setF({ ...f, target_amount: e.target.value })} data-testid="goal-amount" /></div>
        <div><Label>Already saved ({currency})</Label><Input type="number" value={f.current_amount} onChange={(e) => setF({ ...f, current_amount: e.target.value })} data-testid="goal-saved" /></div>
        <div><Label>Target date</Label><Input type="date" min={today()} value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} data-testid="goal-date" /></div>
        <div className="flex items-center justify-between rounded-md border border-border px-3">
          <div><p className="text-sm font-medium">Auto-track earnings</p><p className="text-xs text-muted-foreground">Count new extra earnings</p></div>
          <Switch checked={f.auto_track} onCheckedChange={(v) => setF({ ...f, auto_track: v })} data-testid="goal-autotrack" />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="goal-save">{existing ? "Save changes" : "Create goal"}</Button>
        <Button variant="ghost" onClick={onDone}>Cancel</Button>
      </div>
    </Card>
  );
}

function Bar({ pct }) {
  return (
    <div className="h-3 rounded-full bg-secondary overflow-hidden">
      <div className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-400 transition-all duration-500" style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

function planText(plan) {
  if (!plan) return "";
  if (plan.parts) return plan.parts.map((p) => `${p.units} ${p.unit_label}`).join(" + ");
  return plan.detail || "";
}

/* ---------- Goal card ---------- */
function GoalCard({ goal, currency }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const del = useMutation({ mutationFn: async () => api.delete(`/goals/${goal.id}`), onSuccess: () => { toast.success("Goal deleted"); qc.invalidateQueries({ queryKey: ["goals"] }); } });
  const st = STATUS[goal.status] || STATUS.on_track;
  const money0 = (n) => money(n, currency);

  if (editing) return <GoalForm currency={currency} existing={{ ...goal, target_amount: String(goal.target_amount), current_amount: String(goal.current_amount) }} onDone={() => { setEditing(false); qc.invalidateQueries({ queryKey: ["goals"] }); }} />;

  return (
    <Card className="p-5 space-y-4" data-testid={`goal-card-${goal.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-2xl">{goal.icon}</span>
          <div className="min-w-0">
            <p className="font-black text-lg truncate">{goal.name}</p>
            <p className="text-xs text-muted-foreground">Target {goal.target_date} · {goal.days_remaining >= 0 ? `${goal.days_remaining} days left` : `${-goal.days_remaining} days ago`}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Badge className={`${st.cls} border-0`} data-testid={`goal-status-${goal.id}`}>{st.label}</Badge>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)} data-testid={`goal-edit-${goal.id}`}><PencilSimple className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => del.mutate()} data-testid={`goal-delete-${goal.id}`}><Trash className="h-4 w-4" /></Button>
        </div>
      </div>

      <div>
        <div className="flex justify-between text-sm mb-1.5">
          <span className="font-semibold">{money0(goal.saved)} <span className="text-muted-foreground font-normal">/ {money0(goal.target_amount)}</span></span>
          <span className="text-muted-foreground">{goal.progress_pct}%</span>
        </div>
        <Bar pct={goal.progress_pct} />
        <p className="text-xs text-muted-foreground mt-1.5">
          {goal.remaining > 0 ? <>{money0(goal.remaining)} to go{goal.auto_earned ? ` · ${money0(goal.auto_earned)} auto-tracked` : ""}</> : "Goal reached — nice work!"}
        </p>
      </div>

      {goal.remaining > 0 && (
        <>
          <div className="rounded-md bg-secondary/40 p-3 text-center" data-testid={`goal-headline-${goal.id}`}>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">You need</p>
            <p className="text-2xl font-black text-primary">{money0(goal.remaining)} more</p>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Per day", goal.deadline.per_day], ["Per week", goal.deadline.per_week], ["Per pay period", goal.deadline.per_period]].map(([l, v]) => (
              <div key={l} className="rounded-md bg-secondary/50 py-2">
                <p className="text-xs text-muted-foreground">{l}</p>
                <p className="font-bold text-sm">{v != null ? money0(v) : "—"}</p>
              </div>
            ))}
          </div>

          {goal.options.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Ways to get there</p>
              {goal.options.map((o) => (
                <div key={o.type_id} className="flex items-center justify-between gap-2 rounded-md border border-border p-2.5" data-testid={`opt-${o.type_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Wallet className="h-4 w-4 text-primary shrink-0" weight="bold" />
                    <div className="min-w-0"><p className="text-sm font-medium truncate">{o.name}</p><p className="text-xs text-muted-foreground">{o.detail}</p></div>
                  </div>
                  <p className="text-xs text-right shrink-0 text-muted-foreground">{money0(o.net)}<br /><span className="text-[10px]">net</span></p>
                </div>
              ))}
              {goal.combination?.available && goal.combination?.parts && (
                <div className="flex items-center justify-between gap-2 rounded-md border border-border p-2.5" data-testid={`opt-combination-${goal.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Sparkle className="h-4 w-4 text-primary shrink-0" weight="fill" />
                    <div className="min-w-0"><p className="text-sm font-medium">Combination</p><p className="text-xs text-muted-foreground">{planText(goal.combination)}</p></div>
                  </div>
                  <p className="text-xs text-right shrink-0 text-muted-foreground">{money0(goal.combination.net)}<br /><span className="text-[10px]">net</span></p>
                </div>
              )}
            </div>
          )}

          {goal.recommended && (planText(goal.recommended)) && (
            <div className="rounded-md bg-primary/10 border border-primary/25 p-3" data-testid={`goal-plan-${goal.id}`}>
              <p className="text-xs font-bold uppercase tracking-wide text-primary flex items-center gap-1.5"><Sparkle className="h-4 w-4" weight="fill" /> Recommended plan</p>
              <p className="text-sm mt-1">{planText(goal.recommended)}</p>
              <p className="text-xs text-muted-foreground mt-1">Est. gross {money0(goal.recommended.gross)} · Est. net {money0(goal.recommended.net)}</p>
            </div>
          )}

          {!goal.feasible && (
            <p className="text-xs text-amber-400 flex items-center gap-1.5"><Warning className="h-4 w-4" /> This may be tough in the time left — consider extending the date or lowering the amount.</p>
          )}
        </>
      )}

      {goal.warnings?.length > 0 && (
        <div className="text-xs text-muted-foreground space-y-1">
          {goal.warnings.map((w, i) => <p key={i} className="flex items-start gap-1.5"><Warning className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-400" /> {w}</p>)}
        </div>
      )}
    </Card>
  );
}

/* ---------- What-if ---------- */
function WhatIf({ currency, rates, etypes }) {
  const [f, setF] = useState({ target_amount: "6000", current_amount: "1000", target_date: "" });
  const [perMonth, setPerMonth] = useState({});
  const sim = useMutation({
    mutationFn: async () => (await api.post("/goals/simulate", {
      target_amount: Number(f.target_amount), current_amount: Number(f.current_amount || 0), target_date: f.target_date,
      plan: etypes.map((t) => ({ type_id: t.id, units_per_month: Number(perMonth[t.id] || 0) })).filter((p) => p.units_per_month > 0),
    })).data,
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const money0 = (n) => money(n, currency);
  const p = sim.data?.projection;
  const rev = sim.data?.reverse;
  const hasPlan = etypes.some((t) => Number(perMonth[t.id]) > 0);
  const unitWord = (t) => t.calc_type === "hourly" ? "hrs" : t.calc_type === "per_shift" ? "shifts" : t.calc_type === "per_project" ? "projects" : t.calc_type === "per_assignment" ? "assignments" : "times";

  return (
    <Card className="p-5" data-testid="whatif-card">
      <div className="flex items-center gap-2 mb-1"><Calculator className="h-5 w-5 text-primary" weight="bold" /><p className="font-bold">What-if planner</p></div>
      <p className="text-xs text-muted-foreground mb-4">Fill in how much you can work each month to see if you'll make it — <span className="text-foreground">or leave those blank</span> and we'll tell you how much per month you need. Uses your real rates.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label>Goal ({currency})</Label><Input type="number" value={f.target_amount} onChange={(e) => setF({ ...f, target_amount: e.target.value })} data-testid="whatif-target" /></div>
        <div><Label>Saved</Label><Input type="number" value={f.current_amount} onChange={(e) => setF({ ...f, current_amount: e.target.value })} data-testid="whatif-saved" /></div>
        <div><Label>By date</Label><Input type="date" min={today()} value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} data-testid="whatif-date" /></div>
      </div>
      {etypes.length > 0 && (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mt-3">
          {etypes.map((t) => (
            <div key={t.id}>
              <Label className="text-xs">{t.name} / month ({unitWord(t)})</Label>
              <Input type="number" placeholder="auto" value={perMonth[t.id] || ""} onChange={(e) => setPerMonth({ ...perMonth, [t.id]: e.target.value })} data-testid={`whatif-unit-${t.id}`} />
            </div>
          ))}
        </div>
      )}
      <Button className="mt-3" onClick={() => sim.mutate()} disabled={sim.isPending || !f.target_date} data-testid="whatif-run"><TrendUp className="h-4 w-4 mr-1.5" weight="bold" /> Calculate</Button>

      {sim.data && !hasPlan && rev?.available && (
        <div className="mt-4 rounded-md border border-border p-4" data-testid="whatif-reverse">
          <p className="font-bold flex items-center gap-2"><Calculator className="h-5 w-5 text-primary" weight="fill" /> To close {money0(sim.data.remaining)} by then, work about:</p>
          <p className="text-xs text-muted-foreground mt-0.5">{sim.data.months_remaining} months left · roughly {money0(rev.required_monthly_gross)} gross / {money0(rev.required_monthly_net)} net per month</p>
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            {rev.per_type.map((r) => (
              <div key={r.type_id} className="rounded-md bg-secondary/50 p-3" data-testid={`reverse-${r.type_id}`}>
                <p className="text-sm font-bold">{r.name}</p>
                <p className="text-2xl font-black text-primary leading-tight">{r.units_per_month}<span className="text-sm font-medium text-muted-foreground"> {r.unit_label}/mo</span></p>
                <p className="text-xs text-muted-foreground">{money0(r.monthly_gross)} gross · {money0(r.monthly_net)} net / month</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Each option on its own gets you there — pick whichever fits your schedule, or combine them.</p>
        </div>
      )}

      {sim.data && hasPlan && p && (
        <div className="mt-4 rounded-md border border-border p-4" data-testid="whatif-result">
          <div className="flex items-center gap-2 mb-3">
            {p.reached ? <CheckCircle className="h-6 w-6 text-emerald-400" weight="fill" /> : <Warning className="h-6 w-6 text-amber-400" weight="fill" />}
            <p className="font-bold">{p.reached ? `You'll reach it — with ${money0(p.surplus)} to spare!` : `Short by ${money0(p.remaining_after)} by that date`}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            {[["Monthly gross", p.monthly_gross], ["Monthly net", p.monthly_net], ["Total extra (net)", p.total_extra_net], ["Projected total", p.projected_total]].map(([l, v]) => (
              <div key={l} className="rounded-md bg-secondary/50 py-2"><p className="text-xs text-muted-foreground">{l}</p><p className="font-bold text-sm">{money0(v)}</p></div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">{sim.data.months_remaining} months · {sim.data.periods_remaining} pay periods remaining</p>
        </div>
      )}
    </Card>
  );
}
